/**
 * Parser de estado de cuenta Inbursa empresarial (PDF texto extraíble).
 *
 * Formato:
 *   FECHA  REFERENCIA  CONCEPTO       CARGOS    ABONOS    SALDO
 *   ENE.09 34774026   TRASPASO SPEI INBURED  35,000.00          38,990.84
 *                     KONFIO CREDITO
 *                     BBVA MEXICO 0129...
 *                     036INBU09... RFC NO DISPONIBLE
 *
 * Las filas son multi-línea. La primera tiene fecha + referencia + concepto
 * + monto + saldo. Las siguientes son detalle (RFC, nombre, banco destino).
 *
 * Patterns:
 *   TRASPASO SPEI INBURED  → transferencia (cargo o abono según columna)
 *   DEPOSITO SPEI          → ingreso entrante
 *   COMISION POR MOVIMIENTOS → gasto comisión
 *   IVA COMISION POR ...   → IVA de la comisión anterior
 *   INTERESES GANADOS      → ingreso (intereses bancarios)
 *   ISR RETENIDO           → gasto (retención del SAT por intereses)
 *   ADMINISTRACION - RENTA → gasto comisión
 *   IVA ADMINISTRACION     → IVA de la anterior
 */
import type { BulkParseResult, BulkParsedRow } from "./types";

const MES_MAP: Record<string, number> = {
  ENE: 1,
  FEB: 2,
  MAR: 3,
  ABR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AGO: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DIC: 12,
};

const RFC_PROPIO = "NOB230522884";

/**
 * Punto de entrada — detecta variante (oficial mensual vs Estado de Cuenta
 * Individual provisional) y delega al parser correspondiente.
 */
export function parseInbursa(text: string): BulkParseResult {
  if (/Estado\s+de\s+Cuenta\s+Individual/i.test(text)) {
    return parseInbursaIndividual(text);
  }
  return parseInbursaOficial(text);
}

/**
 * Conceptos típicos en Inbursa → kind. Cuando un concepto matchea, no
 * dependemos del diff de saldos (que puede fallar por filas multi-página
 * o contaminación con el resumen al final del PDF).
 */
const INBURSA_CONCEPT_KIND: Array<{ pattern: RegExp; kind: "ingreso" | "gasto" }> = [
  { pattern: /TRASPASO\s+SPEI/i, kind: "gasto" }, // saliente
  { pattern: /RETIRO/i, kind: "gasto" },
  { pattern: /COMISION/i, kind: "gasto" },
  { pattern: /^IVA\b/i, kind: "gasto" },
  { pattern: /ISR\s+RETENIDO/i, kind: "gasto" },
  { pattern: /ADMINISTRACION\s*-\s*RENTA/i, kind: "gasto" },
  { pattern: /CARGO/i, kind: "gasto" },
  { pattern: /DEPOSITO\s+SPEI/i, kind: "ingreso" },
  { pattern: /DEPOSITO/i, kind: "ingreso" },
  { pattern: /INTERESES\s+GANADOS/i, kind: "ingreso" },
  { pattern: /RENDIMIENTOS/i, kind: "ingreso" },
  { pattern: /ABONO/i, kind: "ingreso" },
];

function parseInbursaOficial(text: string): BulkParseResult {
  const errors: string[] = [];
  const rows: BulkParsedRow[] = [];

  // Periodo: "Del 01 Ene. 2026 al 31 Ene. 2026" o variantes.
  const periodMatch = text.match(
    /Del\s+(\d{2})\s+(\w{3,4})\.?\s+(\d{4})\s+al\s+(\d{2})\s+(\w{3,4})\.?\s+(\d{4})/i,
  );
  const year = periodMatch ? parseInt(periodMatch[3]!, 10) : new Date().getFullYear();
  const periodStart = periodMatch
    ? toIso(periodMatch[1]!, periodMatch[2]!, periodMatch[3]!)
    : undefined;
  const periodEnd = periodMatch
    ? toIso(periodMatch[4]!, periodMatch[5]!, periodMatch[6]!)
    : undefined;

  const accountMatch = text.match(/CUENTA\s+(\d{8,})/i);
  const accountHint = accountMatch?.[1];

  // Saldo actual (del header del resumen) y saldo anterior (saldo inicial).
  const saldoMatch = text.match(/SALDO\s+ACTUAL\s+([-\d,]+\.\d{2})/i);
  const closingBalanceCents = saldoMatch ? parsePesos(saldoMatch[1]!) : undefined;
  const openingMatch = text.match(/SALDO\s+ANTERIOR\s+([-\d,]+\.\d{2})/i);
  const openingCents = openingMatch ? parsePesos(openingMatch[1]!) : null;

  const lines = text.split(/\n/).map((l) => l.trim());
  const rowSig = /^([A-Z]{3,4})\.?\s*(\d{2})\s*(.*)$/;
  // Patrón Inbursa antiguo (2024): "OCT. 3148262214 TRASPASO SPEI INBURED"
  // sin día. El día aparece embebido como YYYYMMDD en una línea de
  // continuación (ej. "20241003 Transferencia entre cuentas propias").
  const rowSigNoDay =
    /^([A-Z]{3,4})\.\s+(?!BALANCE\s+INICIAL)(\d{6,}|TRASPASO|DEPOSITO|COMISION|IVA|INTERESES|ISR|ADMINISTRACION|CARGO|ABONO)/i;
  const yyyymmddRe = /\b(\d{4})(\d{2})(\d{2})\b/;

  // Detectar fin del bloque de movimientos. Inbursa termina la tabla con
  // líneas tipo "Estimado cliente", "Saldo Inicial", "Otros Cargos",
  // "Para cualquier solicitud", "BANCO INBURSA, S.A.". Sin esto, la última
  // fila acumula todo el resumen y los números de ahí se interpretan como
  // saldo / cargos falsos (caso real: parser tomaba $111,057.97 de "Otros
  // Cargos" como movimiento extra).
  const isFooterLine = (line: string) =>
    /^Estimado\s+cliente/i.test(line) ||
    /^Saldo\s+(Inicial|Final|Promedio)/i.test(line) ||
    /^Dep[oó]sitos\s+\d/i.test(line) ||
    /^Otros\s+Cargos/i.test(line) ||
    /^Para\s+cualquier\s+(solicitud|aclaraci)/i.test(line) ||
    /^BANCO\s+INBURSA/i.test(line) ||
    /^TOTAL\s+DE\s+CARGOS/i.test(line) ||
    /^TOTAL\s+DE\s+ABONOS/i.test(line) ||
    /^REGIMEN\s+FISCAL/i.test(line) ||
    /^AVENIDA\s+PASEO/i.test(line);

  type Partial = {
    row: BulkParsedRow;
    pdfSaldoCents: number | null;
    ambiguous: boolean;
  };
  const partial: Partial[] = [];

  let lastSaldoCents: number | null = openingCents;
  let cur: { date: string; firstLine: string; extras: string[] } | null = null;
  // En los PDFs de Inbursa 2024 el formato es invertido: los números
  // (referencia + cargo/abono + saldo) salen ANTES de la línea de fecha:
  //   3079942271    2,567.00   4,056.11
  //   AGO 05   TRASPASO SPEI INBURED
  // Buffer la última línea que parece "solo números" para inyectarla a
  // la siguiente fila cuando llegue sin monto en su línea principal.
  let pendingNumbersLine: string | null = null;
  const looksLikeNumbersOnly = (l: string) =>
    /\d{1,3}(?:,\d{3})*\.\d{2}/.test(l) && !/[A-Za-zÁ-ú]{4,}/.test(l.replace(/[A-Z]{3,4}/g, "")); // sin palabras de 4+ letras (concepto)

  const flush = () => {
    if (!cur) return;
    const full = [cur.firstLine, ...cur.extras].join("\n");
    if (/balance\s+inicial/i.test(full) || /saldo\s+anterior/i.test(full)) {
      const m = full.match(/([\d,]+\.\d{2})/);
      if (m) lastSaldoCents = parsePesos(m[1]!);
      cur = null;
      return;
    }
    // Si la fecha terminó como placeholder "-01" (Inbursa antiguo sin día),
    // buscar un YYYYMMDD en las continuaciones y extraer el día real.
    let finalDate = cur.date;
    if (finalDate.endsWith("-01")) {
      const ymd = full.match(yyyymmddRe);
      if (ymd) {
        const yy = ymd[1]!;
        const mm = ymd[2]!;
        const dd = ymd[3]!;
        // Validar que el mes coincida con el del periodo (defensa contra
        // YYYYMMDD que es realmente otra cosa).
        if (yy === String(year) && parseInt(mm, 10) >= 1 && parseInt(mm, 10) <= 12) {
          finalDate = `${yy}-${mm}-${dd}`;
        }
      }
    }
    const parsed = parseInbursaFullRow(finalDate, full, lastSaldoCents);
    if (parsed) {
      partial.push({
        row: parsed.row,
        pdfSaldoCents: parsed.pdfSaldoCents,
        ambiguous: parsed.ambiguous,
      });
      if (parsed.newSaldoCents !== null) lastSaldoCents = parsed.newSaldoCents;
    } else if (cur.firstLine.length > 5) {
      errors.push(`No pude parsear: ${cur.firstLine.slice(0, 100)}`);
    }
    cur = null;
  };

  for (const line of lines) {
    if (!line) continue;
    if (isFooterLine(line)) {
      flush();
      pendingNumbersLine = null;
      continue;
    }
    const m = line.match(rowSig);
    const mNoDay = !m ? line.match(rowSigNoDay) : null;
    if (m && MES_MAP[m[1]!.toUpperCase()]) {
      flush();
      const month = MES_MAP[m[1]!.toUpperCase()]!;
      const day = parseInt(m[2]!, 10);
      const date = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      // PDFs de Inbursa 2024: si la línea principal no tiene monto pero
      // teníamos uno pendiente (línea-solo-números antes de la fecha),
      // lo prepend al firstLine para que parseInbursaFullRow lo capture.
      const hasAmount = /\d{1,3}(?:,\d{3})*\.\d{2}/.test(line.replace(rowSig, ""));
      const firstLine = !hasAmount && pendingNumbersLine ? `${line} ${pendingNumbersLine}` : line;
      cur = { date, firstLine, extras: [] };
      pendingNumbersLine = null;
    } else if (mNoDay && MES_MAP[mNoDay[1]!.toUpperCase()]) {
      // Inbursa antiguo (2024) sin día: dejamos el día como placeholder
      // "01" y lo corregiremos en flush() leyendo YYYYMMDD de las
      // continuaciones.
      flush();
      const month = MES_MAP[mNoDay[1]!.toUpperCase()]!;
      const placeholderDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const hasAmount = /\d{1,3}(?:,\d{3})*\.\d{2}/.test(line.replace(rowSigNoDay, ""));
      const firstLine = !hasAmount && pendingNumbersLine ? `${line} ${pendingNumbersLine}` : line;
      cur = { date: placeholderDate, firstLine, extras: [] };
      pendingNumbersLine = null;
    } else if (cur) {
      if (/^p[áa]gina\s+\d/i.test(line) || /^banco\s+inbursa/i.test(line)) continue;
      if (/^FECHA\s+REFERENCIA/i.test(line)) continue;
      // En PDFs de Inbursa 2024 los números de la próxima fila aparecen
      // ANTES de su línea de fecha, mientras cur sigue acumulando
      // continuaciones de la fila previa. Si vemos una línea solo-números
      // y la fila actual YA tiene monto, cerramos cur y buffer la línea
      // para inyectarla en la siguiente fila DD-mes.
      if (looksLikeNumbersOnly(line)) {
        const curHasAmount =
          /\d{1,3}(?:,\d{3})*\.\d{2}/.test(cur.firstLine) ||
          cur.extras.some((e) => /\d{1,3}(?:,\d{3})*\.\d{2}/.test(e));
        if (curHasAmount) {
          flush();
          pendingNumbersLine = line.trim();
          continue;
        }
      }
      cur.extras.push(line);
    } else if (looksLikeNumbersOnly(line)) {
      pendingNumbersLine = line.trim();
    }
  }
  flush();

  // 2da pasada: resolver filas ambiguas usando saldos del PDF como anclas.
  if (openingCents !== null) {
    resolveInbursaAmbiguous(partial, openingCents);
  }
  rows.push(...partial.map((p) => p.row));

  return {
    bank: "inbursa",
    accountHint,
    periodStart,
    periodEnd,
    closingBalanceCents,
    rows,
    errors,
  };
}

/**
 * Resolución por anclas similar al parser BBVA. Cada fila con saldo
 * confirmado del PDF actúa como ancla; las filas ambiguas entre dos
 * anclas se resuelven por brute force 2^N para cuadrar el diff.
 */
function resolveInbursaAmbiguous(
  partial: Array<{ row: BulkParsedRow; pdfSaldoCents: number | null; ambiguous: boolean }>,
  openingCents: number,
): void {
  let lastAnchor = openingCents;
  let lastAnchorIdx = -1;
  const settleBlock = (endIdx: number, endAnchor: number) => {
    const indices: number[] = [];
    for (let j = lastAnchorIdx + 1; j <= endIdx; j++) indices.push(j);
    if (indices.length === 0) return;
    let sumFixed = 0;
    const ambiguousIdx: number[] = [];
    for (const j of indices) {
      const r = partial[j]!;
      if (r.ambiguous) ambiguousIdx.push(j);
      else sumFixed += (r.row.kind === "ingreso" ? 1 : -1) * r.row.amountCents;
    }
    const totalDiff = endAnchor - lastAnchor;
    const required = totalDiff - sumFixed;
    if (ambiguousIdx.length === 0) return;
    if (ambiguousIdx.length === 1) {
      const j = ambiguousIdx[0]!;
      const amt = partial[j]!.row.amountCents;
      if (Math.abs(required - amt) <= 100) partial[j]!.row.kind = "ingreso";
      else if (Math.abs(required + amt) <= 100) partial[j]!.row.kind = "gasto";
      return;
    }
    const n = ambiguousIdx.length;
    if (n > 12) return;
    for (let mask = 0; mask < 1 << n; mask++) {
      let sum = 0;
      for (let k = 0; k < n; k++) {
        const sign = mask & (1 << k) ? 1 : -1;
        sum += sign * partial[ambiguousIdx[k]!]!.row.amountCents;
      }
      if (Math.abs(sum - required) <= 100) {
        for (let k = 0; k < n; k++) {
          partial[ambiguousIdx[k]!]!.row.kind = mask & (1 << k) ? "ingreso" : "gasto";
        }
        return;
      }
    }
  };
  for (let i = 0; i < partial.length; i++) {
    const anchor = partial[i]!.pdfSaldoCents;
    if (anchor !== null) {
      settleBlock(i, anchor);
      lastAnchor = anchor;
      lastAnchorIdx = i;
    }
  }
}

/**
 * Parsea una fila Inbursa. Estrategia:
 *   1. amountCents = PENÚLTIMO número cuando hay ≥2 (típico CARGO/ABONO +
 *      SALDO). Si solo hay 1 número, ese es el monto.
 *   2. kind = primer pattern de INBURSA_CONCEPT_KIND que matchee la
 *      primera línea. Si ninguno matchea, marca como ambigua; la 2da
 *      pasada usa anclas de saldo para resolver.
 *   3. pdfSaldoCents = último número si la fila tenía ≥2 números (eso
 *      indica que sí hay columna SALDO).
 *
 * NO derivamos el monto del diff de saldos: cuando la última fila absorbe
 * texto del resumen del PDF ("Otros Cargos 111,057.97"), el diff explota.
 */
function parseInbursaFullRow(
  date: string,
  fullText: string,
  prevSaldoCents: number | null,
): {
  row: BulkParsedRow;
  newSaldoCents: number | null;
  pdfSaldoCents: number | null;
  ambiguous: boolean;
} | null {
  // Extraer todos los números $X,XXX.XX. La primera línea del PDF contiene
  // la referencia + concepto + monto + saldo en columnas. El extractor de
  // pdfjs preserva el orden de izquierda a derecha.
  // Quitar el número de referencia inicial (8+ dígitos sin coma/punto)
  // antes de tokenizar, para no confundirlo con un monto.
  const firstLine = fullText.split("\n")[0] ?? fullText;
  const afterRef = firstLine.replace(/^[A-Z]{3,4}\.?\s*\d{2}\s+\d{6,}\s+/, "");

  // Captura SOLO la primera línea para los montos relevantes — las
  // continuaciones tienen claves de rastreo, RFCs y referencias que
  // pueden lucir como montos con formato similar.
  const numMatches = [...afterRef.matchAll(/(\d{1,3}(?:,\d{3})*\.\d{2})/g)].map((m) => m[1]!);
  if (numMatches.length < 1) return null;

  // Monto = penúltimo si hay ≥2 (porque el último es SALDO), o el único.
  const movementAmount =
    numMatches.length >= 2
      ? parsePesos(numMatches[numMatches.length - 2]!)
      : parsePesos(numMatches[0]!);
  if (movementAmount <= 0) return null;
  let amountCents = movementAmount;

  // Saldo del PDF (último número de la fila, si hay al menos 2).
  const pdfSaldoCents =
    numMatches.length >= 2 ? parsePesos(numMatches[numMatches.length - 1]!) : null;

  // Kind por concepto. Si ningún pattern matchea, marca como ambigua.
  let kind: "ingreso" | "gasto" = "gasto"; // default tentativo
  let matched = false;
  for (const { pattern, kind: k } of INBURSA_CONCEPT_KIND) {
    if (pattern.test(afterRef) || pattern.test(fullText)) {
      kind = k;
      matched = true;
      break;
    }
  }
  const ambiguous = !matched;

  // newSaldoCents para tracking del lastSaldo (proyectado si la fila no
  // trae saldo confiable).
  const direction = kind === "ingreso" ? 1 : -1;
  const projected = prevSaldoCents !== null ? prevSaldoCents + direction * amountCents : null;
  const newSaldoCents = pdfSaldoCents ?? projected;

  // Detectar transferencia interna entre cuentas propias.
  let isInternalDir: "transfer_out" | "transfer_in" | null = null;
  const propio =
    fullText.includes(RFC_PROPIO) ||
    /NUCLEO\s+OBSIDIANA/i.test(fullText) ||
    /BBVA\s+MEXICO\s+\d/i.test(fullText) ||
    /KONFIO/i.test(fullText);
  if (propio) {
    isInternalDir = kind === "gasto" ? "transfer_out" : "transfer_in";
  }

  // Description: primera línea después de la fecha y referencia.
  const description = fullText
    .replace(/^[A-Z]{3,4}\.?\s*\d{2}\s*\d*\s*/, "")
    .split("\n")[0]!
    .replace(/[\d,]+\.\d{2}/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);

  return {
    newSaldoCents,
    pdfSaldoCents,
    ambiguous,
    row: {
      date,
      description,
      kind,
      amountCents,
      ivaCents: 0,
      ...(isInternalDir
        ? {
            internalTransferHint: {
              kind: isInternalDir,
              matchKey: `${date}-${amountCents}`,
            },
          }
        : {}),
      rawCounterparty: extractCounterpartyInbursa(fullText),
    },
  };
}

function extractCounterpartyInbursa(fullText: string): string | undefined {
  // Buscar nombre en mayúsculas en líneas siguientes.
  const lines = fullText.split("\n");
  for (const line of lines.slice(1)) {
    const m = line.match(/^([A-ZÁÉÍÓÚÑ]+(?:\s+[A-ZÁÉÍÓÚÑ]+){2,})$/);
    if (m && !["BBVA", "MEXICO", "NUCLEO"].includes(m[1]!.split(" ")[0]!)) return m[1];
  }
  return undefined;
}

function parsePesos(s: string): number {
  const n = parseFloat(s.replace(/,/g, ""));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
}

function toIso(day: string, mes: string, year: string): string {
  const m = MES_MAP[mes.slice(0, 3).toUpperCase()];
  if (!m) return year;
  return `${year}-${String(m).padStart(2, "0")}-${day.padStart(2, "0")}`;
}

/**
 * Para fechas NN/NN/YYYY ambiguas (ambos ≤ 12) detecta DD/MM vs MM/DD por
 * rango. Si una fila cae dentro del periodo del header con DD/MM la usa;
 * si cae con MM/DD la usa. Default DD/MM (formato MX).
 *
 * Caso real: Inbursa "Estado de Cuenta Individual" reporta fechas en
 * MM/DD/YYYY dentro de la tabla aunque el header del periodo esté en
 * DD/MM/YYYY (inconsistencia del export web).
 */
function pickDateFormat(
  a: string,
  b: string,
  yyyy: string,
  periodStart?: string,
  periodEnd?: string,
): string {
  const aN = parseInt(a, 10);
  const bN = parseInt(b, 10);
  // Casos no ambiguos: si uno > 12 ya no puede ser mes.
  if (aN > 12) return `${yyyy}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`; // DD/MM
  if (bN > 12) return `${yyyy}-${a.padStart(2, "0")}-${b.padStart(2, "0")}`; // MM/DD

  // Ambos ≤ 12 → probar contra el rango del periodo.
  const ddmm = `${yyyy}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
  const mmdd = `${yyyy}-${a.padStart(2, "0")}-${b.padStart(2, "0")}`;

  if (periodStart && periodEnd) {
    const startMs = Date.parse(periodStart);
    const endMs = Date.parse(periodEnd) + 86400_000; // inclusivo
    const ddmmMs = Date.parse(ddmm);
    const mmddMs = Date.parse(mmdd);
    const ddmmIn = ddmmMs >= startMs && ddmmMs <= endMs;
    const mmddIn = mmddMs >= startMs && mmddMs <= endMs;
    if (ddmmIn && !mmddIn) return ddmm;
    if (mmddIn && !ddmmIn) return mmdd;
    // Ambos caben o ninguno cabe — default a DD/MM.
  }
  return ddmm;
}

/**
 * Parser para "Estado de Cuenta Individual" Inbursa (provisional desde web).
 * Formato distinto al oficial mensual:
 *   - Header: "Estado de Cuenta Individual"
 *   - "Fecha Inicial:DD/MM/YYYY  Fecha Fin:DD/MM/YYYY"
 *   - Tabla con columnas: Fecha | Referencia | Refer. Externa | Refer. Leyenda
 *     | Refer. Numérica | Movimiento | Cargo | Abono | Saldo | Ordenante |
 *     Clave de Rastreo
 *   - Fechas en DD/MM/YYYY
 *   - Movimientos: DEPOSITO INBURED, TRASPASO SPEI INBURED, etc.
 */
/**
 * Inbursa "Estado de Cuenta Individual" provisional. Formato TABULAR
 * COLUMNAR: cada columna es una transacción y las filas son los campos
 * (Fecha, Referencia, Movimiento, Cargo, Abono, Saldo). El extractor por
 * Y devuelve cada "fila visual" como una línea de texto con los valores
 * de cada columna separados por espacios.
 *
 * Ejemplo (extracto real para 4 transacciones):
 *   Fecha
 *   05/05/2026 05/06/2026 05/06/2026 05/08/2026
 *   Referencia
 *   70217TR011421300 118707FS01142806 118707FS01142807 118707FS01143462
 *   Cargo
 *   $10,000.00 $15,000.00 $81,200.00
 *   Abono
 *   $145,000.00
 *   Saldo
 *   $65,016.99
 *   $171,216.99 $161,216.99 $146,216.99
 *
 * Estrategia: extraer las fechas (N columnas), referencias, y luego
 * calcular cargo/abono por diff de saldos consecutivos (que es lo más
 * robusto cuando los cargos/abonos vienen como arrays no alineados).
 */
function parseInbursaIndividual(text: string): BulkParseResult {
  const errors: string[] = [];
  const rows: BulkParsedRow[] = [];

  const fechaInicialMatch = text.match(/Fecha\s+Inicial\s*:\s*(\d{2})\/(\d{2})\/(\d{4})/i);
  const fechaFinMatch = text.match(/Fecha\s+Fin\s*:\s*(\d{2})\/(\d{2})\/(\d{4})/i);
  const periodStart = fechaInicialMatch
    ? `${fechaInicialMatch[3]}-${fechaInicialMatch[2]}-${fechaInicialMatch[1]}`
    : undefined;
  const periodEnd = fechaFinMatch
    ? `${fechaFinMatch[3]}-${fechaFinMatch[2]}-${fechaFinMatch[1]}`
    : undefined;

  const accountMatch = text.match(/Cuenta\s*:\s*(\d{8,})/i);
  const accountHint = accountMatch?.[1];

  // Encontrar las fechas — definen el número de columnas N.
  const dateMatches = [...text.matchAll(/(\d{2})\/(\d{2})\/(\d{4})/g)];
  // Filtrar las del header (Fecha Inicial / Fin) — esas suelen estar
  // antes del título "Fecha" como label de columna.
  const fechaLabelIdx = text.search(/^\s*Fecha\s*$/m);
  const dateRows: string[] = [];
  for (const dm of dateMatches) {
    if (dm.index! < fechaLabelIdx) continue; // header dates
    dateRows.push(pickDateFormat(dm[1]!, dm[2]!, dm[3]!, periodStart, periodEnd));
  }

  if (dateRows.length === 0) {
    return {
      bank: "inbursa",
      accountHint,
      periodStart,
      periodEnd,
      rows,
      errors: ["Estado de Cuenta Individual: no se encontraron fechas de movimientos"],
    };
  }

  // Saldo previo (anterior al primer movimiento) — saldo final del periodo
  // anterior. No está siempre disponible; lo derivamos del header si está.
  // Saldos: capturar todos los $X.XX que NO son cargo o abono. Estrategia:
  // los saldos suelen ser los valores más grandes y aparecen agrupados.
  // En la práctica: todos los $X.XX del documento, después filtramos los
  // que coincidan con cargos/abonos.
  const moneyMatches = [...text.matchAll(/\$\s*([\d,]+\.\d{2})/g)].map((m) => parsePesos(m[1]!));

  // Separar montos en bloques por sus labels. "Cargo" / "Abono" / "Saldo".
  const block = (label: string): number[] => {
    const re = new RegExp(
      `${label}\\s*\\n([\\s\\S]*?)(?=\\n\\s*(?:Cargo|Abono|Saldo|Movimiento|Referencia|Fecha|Leyenda|Concepto|$))`,
      "i",
    );
    const m = text.match(re);
    if (!m) return [];
    return [...m[1]!.matchAll(/\$?\s*([\d,]+\.\d{2})/g)].map((x) => parsePesos(x[1]!));
  };

  let cargos = block("Cargo");
  let abonos = block("Abono");
  let saldos = block("Saldo");

  // Fallback: si los bloques no se detectaron, intentar parsear el doc
  // como Inbursa Oficial (rowStartRegex DD/MM/YYYY).
  if (cargos.length === 0 && abonos.length === 0 && saldos.length === 0) {
    return parseInbursaIndividualFallback(text, periodStart, periodEnd, accountHint);
  }

  void moneyMatches;

  // Asignar movimientos a fechas. Cada fecha tiene UN movimiento (cargo
  // o abono). Asumimos que los saldos están en orden cronológico igual
  // que las fechas. Si tenemos N fechas y N saldos:
  //   diff[i] = saldo[i] - saldo[i-1] (donde saldo[-1] es el anterior al periodo)
  //   diff > 0 → ingreso; < 0 → gasto. amount = |diff|.
  // Si saldos.length < N, intentamos con cargos[]/abonos[] explícitos:
  // contamos elementos y asignamos por orden.
  if (saldos.length === dateRows.length) {
    // Inferir saldo previo desde el primer movimiento si abonos[0]
    // explícito coincide con el diff.
    let prevSaldo: number | null = null;
    if (abonos.length >= 1 && saldos.length >= 1) {
      prevSaldo = saldos[0]! - abonos[0]!;
    } else if (cargos.length >= 1 && saldos.length >= 1) {
      prevSaldo = saldos[0]! + cargos[0]!;
    }
    for (let i = 0; i < dateRows.length; i++) {
      const cur = saldos[i]!;
      const prev = i === 0 ? prevSaldo : saldos[i - 1]!;
      if (prev === null) continue;
      const diff = cur - prev;
      const kind: "ingreso" | "gasto" = diff > 0 ? "ingreso" : "gasto";
      const amountCents = Math.abs(diff);
      if (amountCents === 0) continue;
      rows.push({
        date: dateRows[i]!,
        description: kind === "ingreso" ? "DEPOSITO SPEI INBURED" : "TRASPASO SPEI INBURED",
        kind,
        amountCents,
        ivaCents: 0,
        internalTransferHint: {
          kind: kind === "gasto" ? "transfer_out" : "transfer_in",
          matchKey: `${dateRows[i]}-${amountCents}`,
        },
      });
    }
  } else {
    // Sin saldos completos: usar cargos/abonos directamente. El orden de
    // asignación a las fechas es por posición.
    const ops: Array<{ kind: "ingreso" | "gasto"; amt: number }> = [];
    for (const a of abonos) ops.push({ kind: "ingreso", amt: a });
    for (const c of cargos) ops.push({ kind: "gasto", amt: c });
    // Asumimos N movimientos = N fechas.
    for (let i = 0; i < dateRows.length && i < ops.length; i++) {
      const op = ops[i]!;
      rows.push({
        date: dateRows[i]!,
        description: op.kind === "ingreso" ? "DEPOSITO SPEI INBURED" : "TRASPASO SPEI INBURED",
        kind: op.kind,
        amountCents: op.amt,
        ivaCents: 0,
        internalTransferHint: {
          kind: op.kind === "gasto" ? "transfer_out" : "transfer_in",
          matchKey: `${dateRows[i]}-${op.amt}`,
        },
      });
    }
  }

  const closingBalanceCents = saldos.length > 0 ? saldos[saldos.length - 1] : undefined;

  return {
    bank: "inbursa",
    accountHint,
    periodStart,
    periodEnd,
    closingBalanceCents,
    rows,
    errors,
  };
}

function parseInbursaIndividualFallback(
  text: string,
  periodStart: string | undefined,
  periodEnd: string | undefined,
  accountHint: string | undefined,
): BulkParseResult {
  // Plan B: parser viejo basado en líneas DD/MM/YYYY. Sirve si el PDF
  // tiene formato no-columnar.
  const errors: string[] = [];
  const rows: BulkParsedRow[] = [];
  const lines = text
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const rowStartRegex = /^(\d{2})\/(\d{2})\/(\d{4})(.*)$/;
  let lastSaldoCents: number | null = null;
  let cur: { date: string; lines: string[] } | null = null;
  const flush = () => {
    if (!cur) return;
    const fullText = cur.lines.join(" ");
    const parsed = parseInbursaIndividualRow(cur.date, fullText, lastSaldoCents);
    if (parsed) {
      rows.push(parsed.row);
      if (parsed.newSaldoCents !== null) lastSaldoCents = parsed.newSaldoCents;
    }
    cur = null;
  };
  for (const line of lines) {
    const m = line.match(rowStartRegex);
    if (m) {
      flush();
      const date = pickDateFormat(m[1]!, m[2]!, m[3]!, periodStart, periodEnd);
      cur = { date, lines: [m[4]?.trim() ?? ""] };
    } else if (cur) {
      cur.lines.push(line);
    }
  }
  flush();
  return {
    bank: "inbursa",
    accountHint,
    periodStart,
    periodEnd,
    closingBalanceCents: lastSaldoCents ?? undefined,
    rows,
    errors,
  };
}

function parseInbursaIndividualRow(
  date: string,
  fullText: string,
  prevSaldoCents: number | null,
): { row: BulkParsedRow; newSaldoCents: number | null } | null {
  const nums = [...fullText.matchAll(/\$?\s*(\d{1,3}(?:,\d{3})*\.\d{2})/g)].map((m) => m[1]!);
  if (nums.length < 1) return null;

  // Formato Individual: 2 montos típicamente (cargo|abono + saldo) o 3
  // (cargo + abono + saldo, raro). Último es saldo.
  const hasSaldo = nums.length >= 2;
  const newSaldoCents = hasSaldo ? parsePesos(nums[nums.length - 1]!) : null;

  let kind: "ingreso" | "gasto";
  let amountCents: number;

  if (prevSaldoCents !== null && hasSaldo) {
    const diff = newSaldoCents! - prevSaldoCents;
    if (diff > 0) {
      kind = "ingreso";
      amountCents = diff;
    } else if (diff < 0) {
      kind = "gasto";
      amountCents = -diff;
    } else {
      // Sin cambio de saldo — fallback a keywords + monto explícito.
      const lower = fullText.toLowerCase();
      amountCents = parsePesos(nums[0]!);
      kind =
        /dep[oó]sito/i.test(lower) || /\babono\b/i.test(lower) || /intereses\s+ganados/i.test(lower)
          ? "ingreso"
          : "gasto";
    }
  } else {
    // Sin saldo previo o sin saldo en la fila — keywords.
    const lower = fullText.toLowerCase();
    amountCents = parsePesos(nums[0]!);
    kind =
      /dep[oó]sito/i.test(lower) || /\babono\b/i.test(lower) || /intereses\s+ganados/i.test(lower)
        ? "ingreso"
        : "gasto";
  }

  if (amountCents <= 0) return null;

  let isInternalDir: "transfer_out" | "transfer_in" | null = null;
  const propio =
    fullText.includes(RFC_PROPIO) ||
    /NUCLEO\s+OBSIDIANA/i.test(fullText) ||
    /BBVA\s+MEXICO\s+\d/i.test(fullText) ||
    /KONFIO/i.test(fullText) ||
    /Transferencia\s+entre\s+cuentas/i.test(fullText);
  if (propio) {
    isInternalDir = kind === "gasto" ? "transfer_out" : "transfer_in";
  }

  // Description: el primer chunk de letras antes de los montos.
  let description = fullText;
  for (const n of nums) description = description.replace(n, "");
  description = description.replace(/\$/g, "").replace(/\s+/g, " ").trim().slice(0, 200);

  return {
    newSaldoCents,
    row: {
      date,
      description,
      kind,
      amountCents,
      ivaCents: 0,
      ...(isInternalDir
        ? {
            internalTransferHint: {
              kind: isInternalDir,
              matchKey: `${date}-${amountCents}`,
            },
          }
        : {}),
      rawCounterparty: extractCounterpartyInbursa(fullText),
    },
  };
}
